import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const FORM_DRAFT_KEY = "state_form_draft";
const TOAST_DURATION = 4000;

const api = {
  list: (countryId = "", search = "", isActive = "") => {
    const params = new URLSearchParams();
    if (countryId) params.append("country_id", countryId);
    if (search)    params.append("q", search);
    if (isActive !== "") params.append("is_active", isActive);
    return axiosInstance.get(`/api/superadmin/master/states/?${params}`);
  },
  create: (body) =>
    axiosInstance.post(`/api/superadmin/master/states/`, body),
  update: (id, body) =>
    axiosInstance.patch(`/api/superadmin/master/states/${id}/`, body),
  delete: (id) =>
    axiosInstance.delete(`/api/superadmin/master/states/${id}/`),
  countries: (search = "") => {
    const params = new URLSearchParams();
    if (search) params.append("q", search);
    params.append("is_active", "true");
    return axiosInstance.get(`/api/superadmin/master/countries/?${params}`);
  },
};

const formDraft = {
  save:  (data) => { try { sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(data)); } catch { /**/ } },
  load:  ()     => { try { const r = sessionStorage.getItem(FORM_DRAFT_KEY); return r ? JSON.parse(r) : null; } catch { return null; } },
  clear: ()     => { try { sessionStorage.removeItem(FORM_DRAFT_KEY); } catch { /**/ } },
};

const Icon = {
  Plus: () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>),
  Search: () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" /><path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>),
  Edit: () => (<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M11.5 1.5l2 2L5 12H3v-2L11.5 1.5zM10 3l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  Trash: () => (<svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 4h11M5 4V2.5a.5.5 0 01.5-.5h4a.5.5 0 01.5.5V4M6 7v4M9 7v4M3 4l.8 8.5A1 1 0 004.8 13h5.4a1 1 0 001-.95L12 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  Close: () => (<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  ChevronLeft: () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  ChevronRight: () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  ChevronDown: () => (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  Warning: () => (<svg width="48" height="48" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="24" fill="#FEF3C7" /><path d="M24 13L11 37h26L24 13z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" fill="#FDE68A" /><path d="M24 22v7M24 31.5v1" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" /></svg>),
  Restore: () => (<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M11 6.5A4.5 4.5 0 112 4.5M2 2v2.5h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  // ── NEW: checkmark circle icon for activate confirm dialog ──
  CheckCircle: () => (<svg width="48" height="48" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="24" fill="#DCFCE7" /><circle cx="24" cy="24" r="14" stroke="#16a34a" strokeWidth="2" fill="#BBF7D0" /><path d="M17 24l5 5 9-9" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
};

const inputBase = (hasError) => ({
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13.5, outline: "none",
  border: `1.5px solid ${hasError ? "#fca5a5" : "#e2e8f0"}`,
  background: hasError ? "#fff5f5" : "#f8fafc", color: "#0f172a",
  transition: "border 0.15s, background 0.15s", boxSizing: "border-box", fontFamily: "inherit",
});

const btnStyle = (variant = "primary") => {
  const base = { padding: "8px 18px", borderRadius: 8, fontSize: 13.5, fontWeight: 570, cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 7, transition: "all 0.15s", fontFamily: "inherit", letterSpacing: "0.01em" };
  if (variant === "primary")   return { ...base, background: "#1d4ed8", color: "#fff", boxShadow: "0 1px 3px rgba(29,78,216,0.3)" };
  if (variant === "ghost")     return { ...base, background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0" };
  if (variant === "danger")    return { ...base, background: "#dc2626", color: "#fff", boxShadow: "0 1px 3px rgba(220,38,38,0.3)" };
  if (variant === "success")   return { ...base, background: "#16a34a", color: "#fff", boxShadow: "0 1px 3px rgba(22,163,74,0.3)" };
  if (variant === "icon-edit") return { ...base, padding: "6px 10px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #dbeafe", fontSize: 12.5 };
  if (variant === "icon-del")  return { ...base, padding: "6px 10px", background: "#fff5f5", color: "#dc2626", border: "1px solid #fee2e2", fontSize: 12.5 };
  if (variant === "icon-success") return { ...base, padding: "6px 10px", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", fontSize: 12.5 };
  return base;
};

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg)  => toast._push({ type: "success", title, msg }),
  error:   (title, msg)  => toast._push({ type: "error",   title, msg }),
  info:    (title, msg)  => toast._push({ type: "info",    title, msg }),
  loading: (title)       => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)          => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{
      position: "relative", overflow: "hidden", background: "#fff",
      borderRadius: 10, borderLeft: `4px solid ${meta.bar}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
      minWidth: 300, maxWidth: 360,
      animation: exiting ? "toastOut 0.28s ease forwards" : "toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: "2px", flexShrink: 0, display: "flex", borderRadius: 4, transition: "color 0.15s", marginTop: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <Icon.Close />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const Modal = ({ isOpen, title, onClose, children, width = 520 }) => {
  const overlayRef = useRef(null);
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div ref={overlayRef} onClick={(e) => e.target === overlayRef.current && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20, animation: "fadeIn 0.18s ease" }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: width, boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)", animation: "scaleIn 0.2s ease", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 650, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</h3>
        </div>
        <div style={{ padding: "20px 24px 24px", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 580, color: error ? "#dc2626" : "#374151", letterSpacing: "0.01em" }}>
      {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
    </label>
    {children}
    {error && <p style={{ margin: "5px 0 0", fontSize: 12, color: "#dc2626" }}>⚠ {error}</p>}
  </div>
);

const StatusBadge = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 580, background: active ? "#f0fdf4" : "#fef2f2", color: active ? "#15803d" : "#dc2626", border: `1px solid ${active ? "#bbf7d0" : "#fecaca"}` }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#16a34a" : "#dc2626", display: "inline-block" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const SkeletonRows = ({ rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i}>
        {[50, 160, 60, 120, 90, 110].map((w, j) => (
          <td key={j} style={{ padding: "14px 20px" }}>
            <div style={{ height: 13, borderRadius: 5, background: "#f1f5f9", width: w, animation: "pulse 1.5s infinite" }} />
          </td>
        ))}
        <td style={{ padding: "14px 20px" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ width: 62, height: 28, borderRadius: 6, background: "#f1f5f9", animation: "pulse 1.5s infinite" }} />
            <div style={{ width: 62, height: 28, borderRadius: 6, background: "#f1f5f9", animation: "pulse 1.5s infinite" }} />
          </div>
        </td>
      </tr>
    ))}
  </>
);

const Pagination = ({ current, total, onChange }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  const start = (current - 1) * PAGE_SIZE + 1;
  const end   = Math.min(current * PAGE_SIZE, total);
  const pages = [];
  for (let i = Math.max(1, current - 2); i <= Math.min(totalPages, current + 2); i++) pages.push(i);

  const PBtn = ({ n }) => (
    <button onClick={() => onChange(n)} style={{ ...btnStyle(n === current ? "primary" : "ghost"), padding: "5px 11px", minWidth: 34, justifyContent: "center" }}>{n}</button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>Showing <strong>{start}–{end}</strong> of <strong>{total}</strong> results</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button disabled={current === 1} onClick={() => onChange(current - 1)} style={{ ...btnStyle("ghost"), padding: "5px 9px", opacity: current === 1 ? 0.4 : 1, cursor: current === 1 ? "not-allowed" : "pointer" }}><Icon.ChevronLeft /></button>
        {pages[0] > 1 && <><PBtn n={1} />{pages[0] > 2 && <span style={{ color: "#94a3b8" }}>…</span>}</>}
        {pages.map(n => <PBtn key={n} n={n} />)}
        {pages[pages.length - 1] < totalPages && <>{pages[pages.length - 1] < totalPages - 1 && <span style={{ color: "#94a3b8" }}>…</span>}<PBtn n={totalPages} /></>}
        <button disabled={current === totalPages} onClick={() => onChange(current + 1)} style={{ ...btnStyle("ghost"), padding: "5px 9px", opacity: current === totalPages ? 0.4 : 1, cursor: current === totalPages ? "not-allowed" : "pointer" }}><Icon.ChevronRight /></button>
      </div>
    </div>
  );
};

// ── CHANGE 1: Stats bar component ──────────────────────────────────────────
const StatsBar = ({ states, loading }) => {
  const total    = states.length;
  const active   = states.filter((s) => s.is_active).length;
  const inactive = total - active;

  const Card = ({ label, count, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, background: bg, border: `1px solid ${border}`, minWidth: 110 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
          {loading ? <div style={{ width: 28, height: 16, borderRadius: 4, background: "#e2e8f0", animation: "pulse 1.5s infinite" }} /> : count}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: "0.02em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <Card label="Total States" count={total}    color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <Card label="Active"       count={active}   color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <Card label="Inactive"     count={inactive} color="#dc2626" bg="#fef2f2" border="#fecaca" />
    </div>
  );
};

// ── CHANGE 2: Confirm Activate dialog (mirrors ConfirmDialog) ───────────────
const ConfirmActivateDialog = ({ isOpen, stateName, onConfirm, onCancel, loading }) => (
  <Modal isOpen={isOpen} title="Confirm Activation" onClose={onCancel} width={420}>
    <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Icon.CheckCircle /></div>
      <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Activate &quot;{stateName}&quot;?</p>
      <p style={{ margin: "0 0 24px", fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>This will mark the state as active and make it available for use.</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={onCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading}
          style={{ ...btnStyle("success"), opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Activating…" : "Yes, Activate"}
        </button>
      </div>
    </div>
  </Modal>
);

const ConfirmDialog = ({ isOpen, stateName, onConfirm, onCancel, loading }) => (
  <Modal isOpen={isOpen} title="Confirm Deactivation" onClose={onCancel} width={420}>
    <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Icon.Warning /></div>
      <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#1e293b" }}>Deactivate &quot;{stateName}&quot;?</p>
      <p style={{ margin: "0 0 24px", fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>This will mark the state as inactive. You can reactivate it later.</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={onCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{ ...btnStyle("danger"), opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Deactivating…" : "Yes, Deactivate"}
        </button>
      </div>
    </div>
  </Modal>
);

const DraftBanner = ({ onRestore, onDiscard }) => (
  <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 16 }}>📝</span>
      <div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#92400e" }}>Unsaved draft found</p>
        <p style={{ margin: 0, fontSize: 12, color: "#b45309" }}>You have a previously unsaved form. Would you like to restore it?</p>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <button onClick={onDiscard} style={{ ...btnStyle("ghost"), padding: "5px 12px", fontSize: 12.5 }}>Discard</button>
      <button onClick={onRestore}
        style={{ ...btnStyle("primary"), padding: "5px 12px", fontSize: 12.5, background: "#d97706", boxShadow: "none" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#b45309"}
        onMouseLeave={(e) => e.currentTarget.style.background = "#d97706"}>
        <Icon.Restore /> Restore
      </button>
    </div>
  </div>
);

const fetchCountryOptions = async (query) => {
  try {
    const { data } = await api.countries(query);
    return data?.results || [];
  } catch { /**/ }
  return [];
};

const SearchableDropdown = ({ value, displayValue, placeholder = "Select…", fetchOptions, onSelect, onClear, hasError = false, renderOption }) => {
  const [open,     setOpen]     = useState(false);
  const [query,    setQuery]    = useState("");
  const [options,  setOptions]  = useState([]);
  const [fetching, setFetching] = useState(false);
  const wrapperRef = useRef(null);
  const timerRef   = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setQuery(""); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setFetching(true);
      try { const results = await fetchOptions(query); setOptions(results); }
      catch { setOptions([]); }
      finally { setFetching(false); }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query, open, fetchOptions]);

  const handleSelect = (opt) => { onSelect(String(opt.id), opt.name, opt); setOpen(false); setQuery(""); };
  const handleClear  = (e)   => { e.stopPropagation(); onClear(); setQuery(""); };

  const triggerStyle = { ...inputBase(hasError), display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none", padding: "9px 10px 9px 12px", borderColor: hasError ? "#fca5a5" : open ? "#1d4ed8" : "#e2e8f0", background: hasError ? "#fff5f5" : open ? "#fff" : "#f8fafc" };

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <div style={triggerStyle} onClick={() => setOpen((o) => !o)}>
        <span style={{ fontSize: 13.5, color: displayValue ? "#0f172a" : "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {displayValue || placeholder}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 6 }}>
          {value && (
            <span onClick={handleClear} title="Clear"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: "#cbd5e1", color: "#fff", cursor: "pointer", fontSize: 10, lineHeight: 1 }}>
              ✕
            </span>
          )}
          <span style={{ color: "#94a3b8", display: "flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
            <Icon.ChevronDown />
          </span>
        </span>
      </div>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 600, overflow: "hidden", animation: "scaleIn 0.15s ease" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><Icon.Search /></span>
              <input autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
                style={{ ...inputBase(false), paddingLeft: 30, fontSize: 13 }}
                onFocus={(e) => { e.target.style.borderColor = "#1d4ed8"; e.target.style.background = "#fff"; }}
                onBlur={(e)  => { e.target.style.borderColor = "#e2e8f0"; e.target.style.background = "#f8fafc"; }} />
            </div>
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {fetching ? (
              <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", textAlign: "center" }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", textAlign: "center" }}>No results found</div>
            ) : (
              options.map((opt) => (
                <div key={opt.id} onClick={() => handleSelect(opt)}
                  style={{ padding: "9px 14px", fontSize: 13.5, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: String(opt.id) === String(value) ? "#1d4ed8" : "#1e293b", background: String(opt.id) === String(value) ? "#eff6ff" : "transparent", fontWeight: String(opt.id) === String(value) ? 600 : 400, transition: "background 0.1s" }}
                  onMouseEnter={(e) => { if (String(opt.id) !== String(value)) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { if (String(opt.id) !== String(value)) e.currentTarget.style.background = String(opt.id) === String(value) ? "#eff6ff" : "transparent"; }}>
                  {renderOption ? renderOption(opt) : opt.name}
                  {String(opt.id) === String(value) && <span style={{ marginLeft: "auto", color: "#1d4ed8", fontSize: 12 }}>✓</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CountryFilterDropdown = ({ value, onChange }) => {
  const [selectedName, setSelectedName] = useState("");
  const handleSelect = (id, name) => { onChange(id); setSelectedName(name); };
  const handleClear  = ()           => { onChange(""); setSelectedName(""); };
  return (
    <div style={{ flex: "0 1 200px", minWidth: "160px" }}>
      <SearchableDropdown
        value={value} displayValue={selectedName} placeholder="Filter by Country"
        fetchOptions={fetchCountryOptions} onSelect={handleSelect} onClear={handleClear}
        renderOption={(opt) => (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {opt.iso2 && <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, letterSpacing: "0.04em", flexShrink: 0 }}>{opt.iso2}</span>}
            {opt.name}
          </span>
        )}
      />
    </div>
  );
};

const EMPTY_FORM = { country_id: "", country_name: "", name: "", code: "", is_active: true };

const StateForm = ({ editData, onSuccess, onClose }) => {
  const isEdit    = !!editData;
  const savedDraft = !isEdit ? formDraft.load() : null;

  const [showBanner, setShowBanner] = useState(!isEdit && !!savedDraft);
  const [form,    setForm]    = useState(isEdit
    ? { country_id: String(editData.country ?? ""), country_name: editData.country_name ?? "", name: editData.name ?? "", code: editData.code ?? "", is_active: editData.is_active ?? true }
    : EMPTY_FORM
  );
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  useEffect(() => {
    if (isEdit || showBanner) return;
    formDraft.save(form);
  }, [form, isEdit, showBanner]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const handleCountrySelect = (id, name) => {
    setForm((p) => ({ ...p, country_id: id, country_name: name }));
    setErrors((p) => ({ ...p, country_id: "" }));
  };

  const handleRestore = () => { if (savedDraft) setForm(savedDraft); setShowBanner(false); };
  const handleDiscard = () => { formDraft.clear(); setShowBanner(false); setForm(EMPTY_FORM); };

  const handleCancel = () => { if (!isEdit) formDraft.clear(); onClose(); };

  const validate = () => {
    const e = {};
    if (!form.country_id)           e.country_id = "Please select a country.";
    if (!form.name.trim())          e.name       = "State name is required.";
    if (!form.code.trim())          e.code       = "State code is required.";
    else if (form.code.trim().length > 5) e.code = "Max 5 characters allowed.";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);

    const tid = toast.loading(isEdit ? "Updating state…" : "Creating state…");
    try {
      const body = {
        country_id: Number(form.country_id),
        name:       form.name.trim(),
        code:       form.code.trim().toUpperCase(),
        is_active:  form.is_active,
      };
      const { data } = isEdit ? await api.update(editData.id, body) : await api.create(body);

      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && typeof data?.data === "object" && !data?.data?.detail) {
          const mapped = {};
          Object.keys(data?.data).forEach((k) => {
            const val = data?.data[k];
            mapped[k] = Array.isArray(val) ? val[0] : String(val);
          });
          setErrors(mapped);
          if (Object.keys(mapped).length > 1) {
            toast.error("Validation Error", "Please fix the highlighted fields.");
          }
          return;
        }
        const msg = data?.data?.detail || `Operation failed (${data?.status}). Please try again.`;
        toast.error(isEdit ? "Update Failed" : "Create Failed", msg);
        return;
      }

      const created = data?.data?.data ?? data?.data ?? data;
      const label   = created?.name || form.name.trim();
      toast.success(
        isEdit ? "State Updated!" : "State Created!",
        isEdit ? `"${label}" has been updated successfully.` : `"${label}" has been created successfully.`
      );
      formDraft.clear();
      onSuccess();
    } catch (err) {
      toast.dismiss(tid);
      if (err?.name === "TypeError") {
        toast.error("Network Error", "Unable to reach the server. Check your connection.");
      } else {
        toast.error(isEdit ? "Update Failed" : "Create Failed", "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = (e) => { e.target.style.borderColor = "#1d4ed8"; e.target.style.background = "#fff"; };
  const blurStyle  = (e, hasErr) => {
    e.target.style.borderColor = hasErr ? "#fca5a5" : "#e2e8f0";
    e.target.style.background  = hasErr ? "#fff5f5" : "#f8fafc";
  };

  return (
    <div>
      {showBanner && <DraftBanner onRestore={handleRestore} onDiscard={handleDiscard} />}

      {errors.__global && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>⚠ {errors.__global}</p>
        </div>
      )}

      <Field label="Country" required error={errors.country_id}>
        <SearchableDropdown
          value={form.country_id}
          displayValue={form.country_name}
          placeholder="Search and select a country…"
          fetchOptions={fetchCountryOptions}
          onSelect={handleCountrySelect}
          onClear={() => { set("country_id", ""); set("country_name", ""); }}
          hasError={!!errors.country_id}
          renderOption={(opt) => (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {opt.iso2 && <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, letterSpacing: "0.04em", flexShrink: 0 }}>{opt.iso2}</span>}
              {opt.name}
            </span>
          )}
        />
      </Field>

      <Field label="State Name" required error={errors.name}>
        <input ref={firstRef} type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Maharashtra" style={inputBase(!!errors.name)}
          onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.name)} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="State Code" required error={errors.code}>
          <input type="text" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())}
            placeholder="e.g. MH" maxLength={5}
            style={{ ...inputBase(!!errors.code), textTransform: "uppercase", letterSpacing: "0.05em" }}
            onFocus={focusStyle} onBlur={(e) => blurStyle(e, !!errors.code)} />
        </Field>

        <Field label="Status">
          <div onClick={() => set("is_active", !form.is_active)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", userSelect: "none", transition: "border 0.15s" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1d4ed8"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}>
            <div style={{ width: 38, height: 22, borderRadius: 11, background: form.is_active ? "#1d4ed8" : "#cbd5e1", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 3, left: form.is_active ? 19 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </div>
            <span style={{ fontSize: 13, color: form.is_active ? "#15803d" : "#64748b", fontWeight: 550 }}>
              {form.is_active ? "Active" : "Inactive"}
            </span>
          </div>
        </Field>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
        <button onClick={handleCancel} disabled={loading} style={{ ...btnStyle("ghost"), opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={handleSubmit} disabled={loading}
          style={{ ...btnStyle("primary"), opacity: loading ? 0.8 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? (isEdit ? "Updating…" : "Creating…") : (isEdit ? "Update State" : "Create State")}
        </button>
      </div>
    </div>
  );
};

const StateMaster = () => {
  const [states,          setStates]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [countryFilter,   setCountryFilter]   = useState("");
  const [activeFilter,    setActiveFilter]    = useState("");
  const [page,            setPage]            = useState(1);
  const [modalOpen,       setModalOpen]       = useState(false);
  const [editData,        setEditData]        = useState(null);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [deleteLoading,   setDeleteLoading]   = useState(false);
  const [deletingId,      setDeletingId]      = useState(null);
  // ── CHANGE 2: new state for activate confirm dialog ──
  const [activateTarget,  setActivateTarget]  = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);
  const searchTimer = useRef(null);

  const fetchStates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list(countryFilter, search, activeFilter);

      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); setLoading(false); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view states."); setLoading(false); return; }
      if (data?.status >= 400) throw new Error("Load failed");

      setStates([...data?.results].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)));
      setPage(1);
    } catch (err) {
      if (err?.name === "TypeError") {
        toast.error("Network Error", "Unable to reach the server. Check your connection.");
      } else {
        toast.error("Failed to Load", "Unable to fetch states. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [countryFilter, search, activeFilter]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(fetchStates, 350);
    return () => clearTimeout(searchTimer.current);
  }, [fetchStates]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeletingId(deleteTarget.id);
    const tid = toast.loading(`Deactivating "${deleteTarget.name}"…`);
    try {
      const { data } = await api.delete(deleteTarget.id);

      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Deactivate failed (${data?.status}). Please try again.`;
        toast.error("Deactivate Failed", msg);
        return;
      }

      toast.success("Deactivated!", data?.data?.message || `"${deleteTarget.name}" has been deactivated successfully.`);
      setDeleteTarget(null);
      fetchStates();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete deactivation. Please try again.");
    } finally {
      setDeleteLoading(false);
      setDeletingId(null);
    }
  };

  // ── CHANGE 2: handleActivate now confirms first, then calls this ──
  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    setActivateLoading(true);
    setDeletingId(activateTarget.id);
    const tid = toast.loading(`Activating "${activateTarget.name}"…`);
    try {
      const { data } = await api.update(activateTarget.id, { is_active: true });

      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Activate failed (${data?.status}). Please try again.`;
        toast.error("Activate Failed", msg);
        return;
      }

      toast.success("Activated!", `"${activateTarget.name}" has been activated successfully.`);
      setActivateTarget(null);
      fetchStates();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete activation. Please try again.");
    } finally {
      setActivateLoading(false);
      setDeletingId(null);
    }
  };

  const paged      = states.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters = search || countryFilter || activeFilter;

  const TH = ({ children, center }) => (
    <th style={{ padding: "11px 20px", textAlign: center ? "center" : "left", fontSize: 11.5, fontWeight: 650, color: "#64748b", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );

  const TD = ({ children, center, muted }) => (
    <td style={{ padding: "13px 20px", fontSize: 13.5, color: muted ? "#94a3b8" : "#1e293b", borderBottom: "1px solid #f8fafc", textAlign: center ? "center" : "left" }}>
      {children}
    </td>
  );

  return (
    <>
      <style>{`
        @keyframes fadeIn   { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn  { from { opacity: 0; transform: scale(0.96) translateY(-8px) } to { opacity: 1; transform: none } }
        @keyframes pulse    { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }
        @keyframes toastIn  { from { opacity: 0; transform: translateX(24px) scale(0.97) } to { opacity: 1; transform: translateX(0) scale(1) } }
        @keyframes toastOut { from { opacity: 1; transform: translateX(0) scale(1) } to { opacity: 0; transform: translateX(28px) scale(0.95) } }
        @keyframes spin     { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar       { width: 6px; height: 6px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
      `}</style>

      <ToastContainer />

      <div style={{ fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: "100vh", background: "#f8fafc" }}>
        <div style={{ padding: "24px" }}>

          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#0f172a" }}>State Master</h1>
              <p style={{ margin: 0, fontSize: 12.5, color: "#94a3b8" }}>Manage states and territories master data</p>
            </div>
            <button onClick={() => { setEditData(null); setModalOpen(true); }} style={btnStyle("primary")}
              onMouseEnter={(e) => e.currentTarget.style.background = "#1e40af"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#1d4ed8"}>
              <Icon.Plus /> Add State
            </button>
          </div>

          {/* ── CHANGE 1: Stats bar above the filter row ── */}
          <StatsBar states={states} loading={loading} />

          <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #e2e8f0", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><Icon.Search /></span>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search states…"
                style={{ ...inputBase(false), paddingLeft: 34 }}
                onFocus={(e) => e.target.style.borderColor = "#1d4ed8"}
                onBlur={(e)  => e.target.style.borderColor = "#e2e8f0"} />
            </div>

            <CountryFilterDropdown value={countryFilter} onChange={setCountryFilter} />

            <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
              style={{ ...inputBase(false), flex: "0 1 140px", cursor: "pointer", appearance: "none" }}>
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {hasFilters && (
              <button onClick={() => { setSearch(""); setCountryFilter(""); setActiveFilter(""); }}
                style={{ ...btnStyle("ghost"), background: "#fff5f5", color: "#dc2626", borderColor: "#fecaca", fontSize: 13 }}>
                Clear
              </button>
            )}
          </div>

          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead>
                  <tr>
                    <TH>Sr No.</TH>
                    <TH>State Name</TH>
                    <TH>Code</TH>
                    <TH>Country</TH>
                    <TH center>Status</TH>
                    <TH>Created At</TH>
                    <TH center>Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows rows={8} />
                  ) : paged.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: "56px 20px", textAlign: "center" }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: "#374151", marginBottom: 5 }}>No states found</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>
                          {hasFilters ? "Try adjusting your filters or search term" : "Click \"Add State\" to get started"}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paged.map((state, idx) => {
                      const isDeleting = deletingId === state.id;
                      return (
                        <tr key={state.id}
                          style={{ transition: "background 0.1s", opacity: isDeleting ? 0.35 : 1, pointerEvents: isDeleting ? "none" : "auto" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#fafbff"}
                          onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                          <TD muted>{(page - 1) * PAGE_SIZE + idx + 1}</TD>
                          <TD><span style={{ fontWeight: 570 }}>{state.name}</span></TD>
                          <TD>
                            <span style={{ background: "#f1f5f9", color: "#475569", padding: "3px 8px", borderRadius: 5, fontSize: 12, fontWeight: 640, letterSpacing: "0.04em", fontFamily: "'Courier New', monospace" }}>
                              {state.code || "—"}
                            </span>
                          </TD>
                          <TD>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {state.country_iso2 && (
                                <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "2px 6px", borderRadius: 4, letterSpacing: "0.04em" }}>
                                  {state.country_iso2}
                                </span>
                              )}
                              <span style={{ color: "#475569", fontSize: 13 }}>{state.country_name || "—"}</span>
                            </div>
                          </TD>
                          <TD center><StatusBadge active={state.is_active} /></TD>
                          <TD muted><span style={{ fontSize: 12.5 }}>{state.created_at || "—"}</span></TD>
                          <td style={{ padding: "10px 20px", borderBottom: "1px solid #f8fafc" }}>
                            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                              <button title="Edit" onClick={() => { setEditData(state); setModalOpen(true); }}
                                style={btnStyle("icon-edit")}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#dbeafe"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "#eff6ff"}>
                                <Icon.Edit /> Edit
                              </button>
                              {state.is_active ? (
                                <button title="Deactivate" onClick={() => setDeleteTarget(state)}
                                  style={btnStyle("icon-del")}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}>
                                  Deactivate
                                </button>
                              ) : (
                                // ── CHANGE 2: open confirm dialog instead of activating directly ──
                                <button title="Activate" onClick={() => setActivateTarget(state)}
                                  style={btnStyle("icon-success")}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "#dcfce7"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "#f0fdf4"}>
                                  Activate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!loading && states.length > PAGE_SIZE && (
              <Pagination current={page} total={states.length} onChange={setPage} />
            )}
          </div>

        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        title={editData ? `Edit — ${editData.name}` : "Add New State"}
        onClose={() => { if (!editData) formDraft.clear(); setModalOpen(false); setEditData(null); }}>
        <StateForm
          editData={editData}
          onClose={() => { if (!editData) formDraft.clear(); setModalOpen(false); setEditData(null); }}
          onSuccess={() => { setModalOpen(false); setEditData(null); fetchStates(); }}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        stateName={deleteTarget?.name}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── CHANGE 2: Activate confirm dialog ── */}
      <ConfirmActivateDialog
        isOpen={!!activateTarget}
        stateName={activateTarget?.name}
        loading={activateLoading}
        onConfirm={handleActivateConfirm}
        onCancel={() => setActivateTarget(null)}
      />
    </>
  );
};

export default StateMaster;